import { useEffect, useMemo, useState } from 'react';
import type { FailureClass } from '@monitor/shared';
import type { AlertWithId } from '../lib/data';
import type { DisplayTone } from '../lib/status';
import { Badge } from '../components/Badge';
import { timeAgo } from '../lib/format';

type WriteState = 'saving' | 'ok' | 'failed';

const STATUS_TEXT: Record<WriteState, string> = { saving: 'saving…', ok: '✓', failed: 'failed' };

// Exhaustive failure-class -> badge tone, mirroring the Record-based tone maps in
// lib/status.ts. A recovery ('ok') alert reads green, not amber.
const FAILURE_TONE: Record<FailureClass, DisplayTone> = {
  ok: 'ok',
  agent_down: 'bad',
  degraded: 'warn',
  monitor_error: 'warn',
};

/** An agent_down alert whose delivery failed is the only case where acknowledging
 *  has an operational effect: it releases the dead-man switch (and un-acknowledging
 *  re-arms it). See the plan's Problem Frame / R3. */
function isDeadman(a: AlertWithId): boolean {
  return a.failureClass === 'agent_down' && a.deliveryFailed;
}

/**
 * Alerts list with per-alert acknowledge control. Presentational and prop-driven
 * (mirrors StatusBoard): `App` owns the live subscriptions and the
 * `acknowledgeAlert` write. `alerts` is the newest-N list for general browsing;
 * `actionable` is the *complete* set still arming the dead-man switch (R8),
 * fetched by its own query so an old arming alert is never hidden past the
 * newest-N window. All alert-supplied text renders as escaped JSX text (never
 * dangerouslySetInnerHTML), so Intercom-derived markup is inert (R6).
 */
export function AlertsView({
  alerts,
  actionable,
  loading,
  nowMs,
  nameFor,
  onAcknowledge,
}: {
  alerts: AlertWithId[];
  actionable: AlertWithId[];
  loading: boolean;
  nowMs: number;
  nameFor: (a: AlertWithId) => string;
  onAcknowledge: (id: string, acknowledged: boolean) => Promise<void>;
}) {
  const [hideAcknowledged, setHideAcknowledged] = useState(false);
  const [writeState, setWriteState] = useState<Record<string, WriteState>>({});

  // The actionable set is fetched separately and may include alerts outside the
  // newest-N `alerts` window; sort it newest-first and drop its members from the
  // main list so nothing renders twice.
  const { actionableSorted, rest } = useMemo(() => {
    const actionableIds = new Set(actionable.map((a) => a.id));
    const actionableSorted = [...actionable].sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
    const rest = alerts.filter((a) => !actionableIds.has(a.id));
    return { actionableSorted, rest };
  }, [alerts, actionable]);

  // Drop write-status entries for alerts no longer displayed in either list so
  // the map can't grow unbounded over a long-lived session. Both lists count as
  // "live" — pruning on `alerts` alone would wipe an in-flight status for an
  // actionable alert that sits outside the newest-N window, re-enabling its
  // button mid-write.
  useEffect(() => {
    setWriteState((s) => {
      const live = new Set([...alerts, ...actionable].map((a) => a.id));
      const kept = Object.keys(s).filter((id) => live.has(id));
      if (kept.length === Object.keys(s).length) return s;
      return Object.fromEntries(kept.map((id) => [id, s[id]]));
    });
  }, [alerts, actionable]);

  // Defer the empty state until the subscription resolves, so a cold load never
  // flashes a false "No alerts" (R1) — same reasoning as StatusBoard.
  if (loading) return <div className="loading">Loading…</div>;

  const visibleRest = hideAcknowledged ? rest.filter((a) => !a.acknowledged) : rest;

  const acknowledge = async (a: AlertWithId) => {
    const next = !a.acknowledged;
    if (isDeadman(a)) {
      const msg = next
        ? 'Acknowledge this alert? This releases the dead-man switch and stops /fail paging for this incident.'
        : 'Un-acknowledge this alert? This re-arms the dead-man switch and resumes /fail paging.';
      if (!window.confirm(msg)) return;
    }
    setWriteState((s) => ({ ...s, [a.id]: 'saving' }));
    try {
      await onAcknowledge(a.id, next);
      setWriteState((s) => ({ ...s, [a.id]: 'ok' }));
    } catch {
      setWriteState((s) => ({ ...s, [a.id]: 'failed' }));
    }
  };

  const row = (a: AlertWithId) => {
    const state = writeState[a.id];
    const statusText = state ? STATUS_TEXT[state] : null;
    return (
      <div className="alert-row" key={a.id} data-deadman={isDeadman(a) ? '' : undefined}>
        <div className="alert-row-head">
          <span className="channel-name">{nameFor(a)}</span>
          <Badge tone={FAILURE_TONE[a.failureClass]}>{a.failureClass}</Badge>
          <span className="alert-kind">{a.kind}</span>
          {a.acknowledged && <Badge tone="neutral">acknowledged</Badge>}
          <span className="alert-time">{timeAgo(a.createdAt, nowMs)}</span>
        </div>
        <div className="alert-reason">{a.reason}</div>
        {isDeadman(a) && (
          <div className="alert-deadman" role="note">
            {a.acknowledged
              ? '⚠ Un-acknowledging re-arms the dead-man switch and resumes /fail paging.'
              : '⚠ Acknowledging releases the dead-man switch and stops /fail paging for this incident.'}
          </div>
        )}
        <div className="alert-row-actions">
          <button onClick={() => acknowledge(a)} disabled={state === 'saving'}>
            {a.acknowledged ? '✓ acknowledged (un-acknowledge)' : 'Acknowledge'}
          </button>
          {statusText && <span className="save-status">{statusText}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="alerts">
      <div className="alerts-head">
        <h2>Alerts</h2>
        <button
          className="link"
          aria-pressed={hideAcknowledged}
          onClick={() => setHideAcknowledged((v) => !v)}
        >
          {hideAcknowledged ? 'Show acknowledged' : 'Hide acknowledged'}
        </button>
      </div>

      {actionableSorted.length > 0 && (
        <section className="alerts-actionable" role="status">
          <h3>{actionableSorted.length} arming the dead-man switch</h3>
          {actionableSorted.map(row)}
        </section>
      )}

      {visibleRest.map(row)}

      {actionableSorted.length === 0 && visibleRest.length === 0 && (
        <div className="warning-panel" role="status">
          {alerts.length === 0 && actionable.length === 0 ? 'No alerts.' : 'No alerts match the filter.'}
        </div>
      )}
    </div>
  );
}
